import { useSelector } from 'react-redux'
import Header from './components/header/Header'
import MenuShell from './components/menu/MenuShell'
import LastCrashMultipliers from '@components/lastCrashMultipliers/LastCrashMultipliers'
import ActivityPanel from './components/activitypanel/ActivityPanel'
import GameCanvas from '@components/canvas/GameCanvas'
import BettingPanel from './components/bettingpanel/BettingPanel'
import OverlayController from '@components/overlays/OverlayController'
import ChatOverlay from './components/chat/ChatOverlay'
import { useActiveJackpot } from '@hooks/jackpot/useActiveJackpot'
import { JACKPOT_BET_RATIO_MESSAGE } from '@constants/jackpotConstants'

const Index = ({ game, theme }) => {
  const backgrounds = game?.background?.type === 'IMAGE' ? game?.background?.source : null

  const { dimensions, mode } = useSelector((state) => state.layout)
  const { jackpot } = useSelector((state) => state.configurations.featureToggles)
  const { isActive: isJackpotActive } = useActiveJackpot()
  const MODE = mode

  const getStyles = (type) => {
    const headerHeight = dimensions?.sizes?.header?.height ?? 0
    const gap = dimensions?.gap ?? 0

    // Header keeps its height in every mode; the gap BELOW it only off-desktop
    // (on desktop the grid sits flush under the header).
    if (type === 'header') {
      return {
        height: headerHeight || null,
        ...(MODE !== 'DESKTOP' && { marginBottom: gap }),
      }
    }

    // Body fills the viewport minus the header — and minus the header's gap off-desktop.
    if (type === 'body') {
      const consumed = MODE === 'DESKTOP' ? headerHeight : headerHeight + gap
      return { height: `calc(100% - ${consumed}px)` }
    }

    // Desktop panels are sized by the CSS grid, so don't inline their heights.
    if (MODE === 'DESKTOP') {
      return {}
    }

    const height = dimensions?.sizes?.[type]?.height

    let extraStyles = {}

    if (type === 'activityPanel') {
      extraStyles = { display: 'none' }
    }

    if (type === 'jackpot' && (!jackpot || !isJackpotActive || MODE === 'MINI')) {
      extraStyles = { display: 'none' }
    }

    return {
      height: height != null && height > 0 ? height : null,
      marginBottom: gap,
      ...extraStyles,
    }
  }

  return (
    <div
      className={`standard-normal-layout-outer-container ${game?.background?.type === 'IMAGE' && 'standard-normal-layout-outer-image-container'}`}
      style={{
        backgroundImage: backgrounds ? `url(${backgrounds[theme]})` : undefined,
      }}
    >
      <div className="standard-normal-layout-inner-container">
        <div className="standard-normal-layout-relative-container">
          <div
            className={`standard-normal-layout-relative-view-header-container ${game?.background?.type === 'IMAGE' && 'standard-normal-layout-relative-view-header-background'}`}
            style={getStyles('header')}
          >
            <Header />
          </div>
          <div
            className="standard-normal-layout-relative-view-body-container"
            style={getStyles('body')}
          >
            <div
              className="snl-relative-view-last-multipliers-panel-container"
              style={getStyles('lastCrashMultipliers')}
            >
              <LastCrashMultipliers />
            </div>

            <div className="snl-relative-view-canvas-panel-container" style={getStyles('canvas')}>
              <GameCanvas />
            </div>

            <div
              className="snl-relative-view-jackpot-message-panel-container"
              style={getStyles('jackpot')}
            >
              {isJackpotActive && JACKPOT_BET_RATIO_MESSAGE}
            </div>

            <div
              className="snl-relative-view-betting-panel-container"
              style={getStyles('bettingPanel')}
            >
              <BettingPanel />
            </div>

            <div
              className="snl-relative-view-activity-panel-container"
              style={getStyles('activityPanel')}
            >
              <ActivityPanel />
            </div>
          </div>
        </div>
        <div className="standard-normal-layout-absolute-container">
          <div className="snl-absolute-view-menu-shell-container">
            <MenuShell />
          </div>
          <div className="snl-absolute-view-overlay-shell-container">
            <OverlayController />
          </div>
          <ChatOverlay />
        </div>
      </div>
    </div>
  )
}

export default Index
